import moment from 'moment'
import { formatNumber } from '../../kit/helpers'
import { ALLOWANCE_METRICS } from '../tenantHelpers'

// Labels, colours and small formatters for the Commercial usage tab.
// The keys mirror the server: USAGE_ACTION_STATUSES in
// server/src/models/AdminCRM/TenantMonthlyUsage.js, the metric lists in
// server/src/helpers/subscriptionPlans.js and the action / reason names in
// server/src/services/usageReviewActions.js - keep them in step.

export const METRIC_LABELS = Object.fromEntries(
  ALLOWANCE_METRICS.map((metric) => [metric.key, metric.label]),
)

// Without the "/ month", for sentences and chips.
export const METRIC_NAMES = {
  employees: 'Employees',
  vehicles: 'Vehicles',
  baseLocations: 'Base locations',
  ptsJourneysPerMonth: 'PTS journeys',
  controlRoomHoursPerMonth: 'Control-room hours',
  concurrentControlRooms: 'Concurrent control rooms',
}

export const METRIC_KINDS = {
  hard: {
    label: 'Enforced capacity',
    color: '#268EBB',
    hint: 'Enforced when records are added, or when a further control room would be started or reopened - an agreed upgrade may be needed first. Never an exceeded month.',
  },
  soft: {
    label: 'Recorded for review only',
    color: '#F28B1F',
    hint: 'Recorded for review only. Going over it never blocks a journey or a control room and never creates a charge.',
  },
  // No metric is in this class any more (concurrent control rooms became
  // enforced capacity on 2026-10-02); kept so an unexpected kind still renders.
  recorded: {
    label: 'Recorded only',
    color: '#64748B',
    hint: 'Tracked and shown for review. It is never an exceedance.',
  },
}

// Concurrently active control rooms are enforced capacity (the server's
// HARD_LIMIT_METRICS): a further room cannot be started or reopened while the
// allowance is in use, and rooms already open are never closed. The shared
// ALLOWANCE_METRICS mirror in ../tenantHelpers.js still says 'recorded', so the
// kind is overridden here until that mirror is updated.
const KIND_OVERRIDES = { concurrentControlRooms: 'hard' }

export const metricKind = (key) =>
  KIND_OVERRIDES[key] ?? ALLOWANCE_METRICS.find((metric) => metric.key === key)?.kind

// The three allowances a quotation may propose and an Enterprise+ transition
// must state a new total for.
export const QUOTE_METRICS = ['ptsJourneysPerMonth', 'controlRoomHoursPerMonth', 'concurrentControlRooms']
export const OPTIONAL_TRANSITION_METRICS = ['employees', 'vehicles', 'baseLocations']

export const INCLUDED_SOURCES = {
  contract: 'accepted contract',
  legacyAgreed: 'legacy agreed limit',
  catalogue: 'plan',
  notIncluded: 'not included',
}

// Tenant commercial status (contract.usageReview.status, derived from the
// monthly records). `short` is the one-word form for the header tile.
export const SEQUENCE_STATUS = {
  clear: { label: 'Clear', short: 'Clear', color: '#00964C' },
  advisoryRequired: { label: 'Advisory required', short: 'Advisory', color: '#F28B1F' },
  quoteRequired: { label: 'Quote required', short: 'Quote', color: '#F28B1F' },
  upgradeRequired: { label: 'Upgrade required', short: 'Upgrade', color: '#E32525' },
  resolved: { label: 'Resolved', short: 'Resolved', color: '#268EBB' },
}

export const sequenceStatusMeta = (status) => SEQUENCE_STATUS[status] ?? SEQUENCE_STATUS.clear

export const OPEN_ACTION_STATUSES = ['advisoryRequired', 'quoteRequired', 'upgradeRequired']

// The action block on one monthly record. `plain` is the required action in
// plain words (dashboard rows, tiles); `todo` is what staff are asked to do.
export const ACTION_STATUS = {
  none: { label: 'No action', color: '#64748B' },
  advisoryRequired: {
    label: 'Advisory required',
    color: '#F28B1F',
    plain: 'advisory email required',
    todo: 'Send the advisory email from your own mailbox, then mark it as sent. No payment or change is asked of the customer.',
  },
  advisorySent: { label: 'Advisory sent', color: '#00964C' },
  quoteRequired: {
    label: 'Quote required',
    color: '#F28B1F',
    plain: 'Enterprise+ quote required',
    todo: 'Send an Enterprise+ quotation and record it - or, where the month was seasonal, record a seasonal review - no change.',
  },
  quoteSent: { label: 'Quote sent', color: '#00964C' },
  upgradeRequired: {
    label: 'Upgrade required',
    color: '#E32525',
    plain: 'Enterprise+ upgrade required',
    todo: 'Check whether the earlier quote still fits (issue a revised one if usage has changed), agree the variation, change the subscription by hand in Stripe, then complete the Enterprise+ transition - or record a seasonal review - no change.',
  },
  upgradeCompleted: { label: 'Upgrade completed', color: '#00964C' },
  seasonalReviewNoChange: { label: 'Seasonal review - no change', color: '#268EBB' },
  voided: { label: 'Voided', color: '#64748B' },
}

export const actionStatusMeta = (status) => ACTION_STATUS[status] ?? { label: status || 'No action', color: '#64748B' }

// Used only when a month with an open action is older than the months the
// panel lists, so the server has not said which actions it allows: just the
// action that closes it (no reset, no drafts - those are only ever offered
// from a record's own `allowedActions` / `emailDraftStages`). The server
// still decides: anything it does not allow comes back as a 409 with a message.
export const FALLBACK_ALLOWED_ACTIONS = {
  advisoryRequired: ['advisory-sent'],
  quoteRequired: ['quote-sent', 'seasonal-review'],
  upgradeRequired: ['quote-sent', 'seasonal-review'],
}

// What staff can record against a month (the keys of a record's
// `allowedActions`), and the dialog each one opens.
export const RECORD_ACTIONS = {
  'advisory-sent': { label: 'Mark advisory sent', dialog: 'advisory' },
  'quote-sent': { label: 'Record quote sent', revisedLabel: 'Record revised quote', dialog: 'quote' },
  'seasonal-review': { label: 'Record seasonal review - no change', dialog: 'seasonal' },
  'customer-informed': { label: 'Mark customer informed', dialog: 'informed' },
  reset: { label: 'Reset after correction', dialog: 'reset', destructive: true },
}

export const recordActionLabel = (key, status) => {
  const action = RECORD_ACTIONS[key]
  if (!action) return key
  return status === 'upgradeRequired' && action.revisedLabel ? action.revisedLabel : action.label
}

export const NOT_MONITORED_REASONS = {
  noAgreement: {
    label: 'no accepted agreement',
    detail: 'The Business Subscription Agreement has not been accepted, so usage is recorded but not assessed against any allowance.',
  },
  beforeMonitoringStart: {
    label: 'before monitoring started',
    detail: 'Monitoring starts with the first full calendar month after the agreement was accepted.',
  },
  reviewDisabled: {
    label: 'usage review switched off',
    detail: 'Usage review is switched off on the contract (Contract & billing tab), so months are recorded but not assessed.',
  },
  planChangedSinceAcceptance: {
    label: 'plan changed since acceptance',
    detail: 'The plan changed after the agreement was accepted. Monitoring is paused until the customer accepts the new Plan Summary.',
  },
  beforeSequenceStart: {
    label: 'before the current sequence',
    detail: 'The month is earlier than the start of the current review sequence (a reset or an Enterprise+ transition started afresh).',
  },
}

export const notMonitoredLabel = (reason) => NOT_MONITORED_REASONS[reason]?.label ?? reason ?? 'not monitored'

export const AGREEMENT_STATUS = {
  accepted: { label: 'Accepted', color: '#00964C' },
  pending: { label: 'Pending', color: '#F28B1F' },
  notRequired: { label: 'Not required', color: '#64748B' },
}

export const AGREEMENT_REASONS = {
  neverAccepted: 'the customer has not accepted the agreement yet',
  newVersion: 'a newer version is waiting to be accepted',
  planChanged: 'the plan changed after the last acceptance, so the new Plan Summary is waiting to be accepted',
}

export const ACCEPTANCE_SOURCES = {
  signup: 'Signup',
  existingCustomer: 'Existing customer (in the app)',
}

export const ACCEPTANCE_REASONS = {
  initial: 'First acceptance',
  newVersion: 'New agreement version',
  planChanged: 'Plan changed',
}

export const CONFIRMATIONS = [
  { key: 'reviewedAgreement', label: 'Reviewed the agreement' },
  { key: 'acceptsForOrganisation', label: 'Accepts it for the organisation' },
  { key: 'hasAuthority', label: 'Has authority to bind the organisation' },
]

export const GRANT_STATUS = {
  active: { label: 'Active', color: '#00964C' },
  scheduled: { label: 'Scheduled', color: '#268EBB' },
  expired: { label: 'Expired', color: '#64748B' },
  revoked: { label: 'Revoked', color: '#B42318' },
}

export const VARIATION_KINDS = {
  contractEdit: 'Contract edit',
  enterprisePlusTransition: 'Enterprise+ transition',
  grantAdded: 'Capacity added',
  grantRevoked: 'Capacity revoked',
  agreementAccepted: 'Agreement accepted',
  sequenceReset: 'Sequence reset',
  usageReviewToggled: 'Usage review toggled',
  signupPlanMismatch: 'Signup plan mismatch',
}

export const STRIPE_CHANGE_LABELS = {
  completed: 'Completed by hand in Stripe',
  scheduled: 'Scheduled by hand in Stripe for the effective date',
}

// Audit-trail action names (staff actions plus the nightly finalisation).
export const HISTORY_ACTIONS = {
  finalised: { label: 'Month finalised', color: '#64748B' },
  finalisedActionRaised: { label: 'Action raised', color: '#F28B1F' },
  advisorySent: { label: 'Advisory sent', color: '#00964C' },
  quoteSent: { label: 'Quote sent', color: '#00964C' },
  quoteRevised: { label: 'Quote revised', color: '#85276A' },
  seasonalReviewNoChange: { label: 'Seasonal review', color: '#268EBB' },
  customerInformed: { label: 'Customer informed', color: '#268EBB' },
  voided: { label: 'Reset after correction', color: '#B42318' },
  renumbered: { label: 'Renumbered', color: '#B54708' },
  upgradeCompleted: { label: 'Upgrade completed', color: '#00964C' },
  grantAdded: { label: 'Capacity added', color: '#239AA4' },
  grantRevoked: { label: 'Capacity revoked', color: '#B42318' },
}

export const historyActionMeta = (action) => HISTORY_ACTIONS[action] ?? { label: action || 'Change', color: '#64748B' }

// Data-quality flags on a control room (CONTROL_ROOM_FLAGS in
// server/src/helpers/usage/controlRoomUsage.js). Review information only.
export const ROOM_FLAGS = {
  missingStarted: 'no start time',
  missingClosed: 'no close time',
  closedBeforeStarted: 'closed before it started',
  reopened: 'reopened (hours are an upper bound)',
  reconstructed: 'session rebuilt from start/close times',
  sessionCloseMissing: 'session close missing',
  sessionOpenMissing: 'session open missing',
}

export const EMAIL_STAGES = {
  advisory: {
    label: 'Advisory email',
    button: 'Draft advisory email',
    recordWith: 'Mark advisory sent',
  },
  quote: {
    label: 'Quote email',
    button: 'Draft quote email',
    recordWith: 'Record quote sent',
  },
  seasonal: {
    label: 'Seasonal review email',
    button: 'Draft seasonal review email',
    recordWith: 'Mark customer informed',
  },
}

export const RECIPIENT_ROLES = {
  owner: 'Owner',
  siteAdmin: 'Site admin',
  agreementSignatory: 'Agreement signatory',
}

// Mirrors MIN_REASON_LENGTH in server/src/services/usageReviewActions.js.
export const MIN_REASON_LENGTH = 10

// How many months the grant preview lists before summarising the rest.
const GRANT_PREVIEW_MAX = 60

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

// Control-room usage is stored in minutes and shown in hours, one decimal.
export const formatHours = (minutes) => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return ''
  return formatNumber(Math.round(minutes / 6) / 10, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

// "612 of 500" for PTS journeys on a record; the allowance may be null.
export const ptsUsageText = (record) => {
  const allowance = record?.pts?.allowanceSnapshot
  return `${formatNumber(record?.pts?.count ?? 0)} of ${
    typeof allowance === 'number' ? formatNumber(allowance) : 'no allowance'
  }`
}

export const controlRoomUsageText = (record) => {
  const allowance = record?.controlRoom?.allowanceMinutesSnapshot
  return `${formatHours(record?.controlRoom?.minutes ?? 0)} of ${
    typeof allowance === 'number' ? formatHours(allowance) : 'no allowance'
  }`
}

// Today as a date-input value (the browser's local day).
export const todayInput = () => moment().format('YYYY-MM-DD')

// The day after a date-input value ('' when it is not a date).
export const dayAfter = (value) => {
  const day = moment(value, 'YYYY-MM-DD', true)
  return day.isValid() ? day.add(1, 'day').format('YYYY-MM-DD') : ''
}

// The London calendar day of an instant from the payload, as a date-input
// value. Formatting only - used to turn a record's own `periodEnd` into the
// earliest day an email about that month can have been sent.
export const londonDateInput = (instant) => {
  const date = new Date(instant)
  if (!instant || Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const part = (type) => parts.find((entry) => entry.type === type)?.value
  return `${part('year')}-${part('month')}-${part('day')}`
}

// The months a complimentary grant can actually change: the server's
// `affectsPeriods`, sent next to `appliesToPeriods` (a finalised month is
// never altered by a grant, and a revoked grant changes nothing). null when
// the payload does not carry it.
export const grantAffectedPeriods = (grant) =>
  Array.isArray(grant?.affectsPeriods) ? grant.affectsPeriods : null

export const isDateInput = (value) => moment(value, 'YYYY-MM-DD', true).isValid()

export const isHttpUrl = (value) => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch (error) {
    return false
  }
}

// A figure typed into a number field: the number, or null when blank or not a
// number.
export const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

// PREVIEW of the months a complimentary grant will count for, from the two
// date-input values. The server's rule: every month from the start date's
// month to the month of (expiry minus an instant) - the expiry is exclusive,
// so 15 March → 1 April covers March only. The saved grant comes back with the
// authoritative list (`appliesToPeriods`).
export const grantPreviewPeriods = (startsAt, expiresAt) => {
  const start = moment(startsAt || todayInput(), 'YYYY-MM-DD', true)
  const expiry = moment(expiresAt, 'YYYY-MM-DD', true)
  if (!start.isValid() || !expiry.isValid() || !expiry.isAfter(start)) return { periods: [], more: 0 }
  const first = start.clone().startOf('month')
  const last = expiry.clone().subtract(1, 'day').startOf('month')
  const total = last.diff(first, 'months') + 1
  const periods = Array.from({ length: Math.min(total, GRANT_PREVIEW_MAX) }, (unused, index) =>
    first.clone().add(index, 'months').format('YYYY-MM'),
  )
  return { periods, more: Math.max(0, total - periods.length) }
}
